import { useEffect, useLayoutEffect, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

const positions = new Map<string, number>()
let lastPathname = ''

/**
 * Scroll behaviour for the app's single page scroller (`[data-page-scroll]`):
 * - new page (PUSH) → starts at the top (it used to inherit the previous
 *   page's scroll offset on phones, so detail pages opened half-way down);
 * - Back/Forward (POP) → returns to where you were on that page;
 * - REPLACE on the same path (filters, tabs, search) → keeps the position.
 */
export function usePageScrollRestoration(ref: RefObject<HTMLElement | null>) {
  const location = useLocation()
  const navType = useNavigationType()
  const { key, pathname } = location

  // Continuously remember this entry's offset (cleanup-time reads are too
  // late — the next page is already in the DOM by then).
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onScroll = () => positions.set(key, el.scrollTop)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [ref, key])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (navType === 'REPLACE' && lastPathname === pathname) {
      positions.set(key, el.scrollTop)
      return
    }
    lastPathname = pathname
    const target = navType === 'POP' ? (positions.get(key) ?? 0) : 0
    el.scrollTop = target
    if (target <= 0) return

    // Cached lists render instantly, but images/sections can grow the page
    // for a few frames — keep nudging until reachable, stop if the user scrolls.
    let frames = 0
    let raf = 0
    let userMoved = false
    const stop = () => {
      userMoved = true
    }
    el.addEventListener('touchstart', stop, { passive: true })
    el.addEventListener('wheel', stop, { passive: true })
    const tick = () => {
      if (userMoved || frames++ > 30) return
      if (Math.abs(el.scrollTop - target) > 2) el.scrollTop = target
      if (Math.abs(el.scrollTop - target) > 2) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('touchstart', stop)
      el.removeEventListener('wheel', stop)
    }
  }, [ref, key, pathname, navType])
}
